import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location]);

  const shoppingGuideSlugs = [
    { slug: 'sugargoo-review', label: 'Sugargoo Review' },
    { slug: 'sugargoo-shipping-cost', label: 'Shipping Cost Guide' },
    { slug: 'sugargoo-coupon-guide', label: 'Coupon & Discounts' },
    { slug: 'sugargoo-warehouse-guide', label: 'Warehouse Guide' },
    { slug: 'sugargoo-beginners-guide', label: 'Beginners Guide' },
    { slug: 'sugargoo-vs-other-agents', label: 'Agent Comparison' },
    { slug: 'best-sugargoo-finds', label: 'Best Finds' },
    { slug: 'sugargoo-qc-guide', label: 'QC Photo Guide' },
    { slug: 'sugargoo-tips-tricks', label: 'Tips & Tricks' },
  ];

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
            : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 md:px-6 flex items-center justify-between h-16">
          <Link
            to="/"
            className={`flex items-center gap-2 transition-colors flex-shrink-0 ${
              scrolled
                ? 'text-foreground-900 hover:text-primary-600'
                : 'text-white hover:text-white/80'
            }`}
          >
            <span className="font-heading text-xl font-bold tracking-tight whitespace-nowrap">
              Sugargoo <span className={scrolled ? 'text-primary-600' : 'text-accent-400'}>Spread</span>
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-5">
            <Link
              to="/"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? location.pathname === '/'
                    ? 'text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                  : location.pathname === '/'
                    ? 'text-accent-400'
                    : 'text-white/80 hover:text-white'
              }`}
            >
              Home
            </Link>

            {/* Shopping Guide Dropdown */}
            <div className="relative group">
              <button className={`text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-1 cursor-pointer ${
                scrolled
                  ? shoppingGuideSlugs.some(g => location.pathname === `/${g.slug}`)
                    ? 'text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                  : shoppingGuideSlugs.some(g => location.pathname === `/${g.slug}`)
                    ? 'text-accent-400'
                    : 'text-white/80 hover:text-white'
              }`}>
                Shopping Guide
                <i className="ri-arrow-down-s-line text-xs"></i>
              </button>
              <div className="absolute top-full left-0 mt-2 w-52 bg-background-50 border border-background-200 rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50 shadow-sm">
                {shoppingGuideSlugs.map((guide) => (
                  <Link
                    key={guide.slug}
                    to={`/${guide.slug}`}
                    className="block px-4 py-2.5 text-sm text-foreground-600 hover:text-primary-600 hover:bg-primary-50/50 transition-colors first:rounded-t-lg last:rounded-b-lg whitespace-nowrap"
                  >
                    {guide.label}
                  </Link>
                ))}
              </div>
            </div>

            <Link
              to="/sugargoo-spreadsheet-guide"
              className={`text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? location.pathname === '/sugargoo-spreadsheet-guide'
                    ? 'text-primary-600'
                    : 'text-foreground-600 hover:text-foreground-900'
                  : location.pathname === '/sugargoo-spreadsheet-guide'
                    ? 'text-accent-400'
                    : 'text-white/80 hover:text-white'
              }`}
            >
              Complete Guide
            </Link>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-4 py-2 rounded-md text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer ml-1"
            >
              Visit Main Site
            </a>
          </div>

          {/* Mobile: hamburger + CTA */}
          <div className="flex md:hidden items-center gap-3">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="bg-primary-500 hover:bg-primary-600 text-background-50 px-3 py-1.5 rounded-md text-xs font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Main Site
            </a>
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="w-9 h-9 flex items-center justify-center text-foreground-700 cursor-pointer"
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileOpen(false)}></div>
          <div className="absolute top-16 right-0 w-72 max-w-[85vw] h-[calc(100vh-4rem)] bg-background-50 overflow-y-auto border-l border-background-200">
            <div className="p-4 space-y-1">
              <Link
                to="/"
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-home-line text-primary-500"></i>
                </span>
                Home
              </Link>
              <Link
                to="/sugargoo-spreadsheet-guide"
                className="flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
              >
                <span className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-book-read-line text-primary-500"></i>
                </span>
                Complete Guide
              </Link>

              <div className="pt-3 pb-1">
                <p className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Shopping Guides</p>
              </div>
              {shoppingGuideSlugs.map((guide) => (
                <Link
                  key={guide.slug}
                  to={`/${guide.slug}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-article-line text-primary-400 text-xs"></i>
                  </span>
                  {guide.label}
                </Link>
              ))}

              <div className="pt-3 pb-1">
                <p className="px-3 text-xs font-semibold text-foreground-400 uppercase tracking-wider">Product Categories</p>
              </div>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-foreground-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={`${cat.icon} text-primary-400 text-xs`}></i>
                  </span>
                  {cat.name}
                </a>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}